import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { storeSettingsService } from "../../services/storeSettings.service";
import { getBusiness } from "../../services/auth.service";
import { getProducts, getServices } from "../../services/inventory.service";
import { SHOPIFY_THEMES, THEME_LAYOUT_PRESETS } from "./storeSetting/ThemeGallery";
import { getStorefrontPath } from "./storefrontPaths";
import { ThemeLayoutContent, getLayoutType } from "./themeTemplates/ThemeLayouts";
import { THEME_IMAGES } from "./themeTemplates/ThemeLayoutHelpers";
import { getStorefrontThemePreset, resolveThemeId } from "./themeTemplates/storefrontThemePresets";

// Helper function to extract editable text values from theme data.
const getThemeText = (theme) => {
  const result = {};

  if (!theme || typeof theme !== 'object') {
    return result;
  }

  const skipKeys = new Set([
    'themeId', 'themeName', 'primaryColor', 'secondaryColor', 'buttonColor',
    'backgroundColor', 'textColor', 'headingColor', 'fontFamily', 'buttonStyle',
    'cardStyle', 'heroVariant', 'productVariant', 'layoutPreset', 'hideBanner',
    'themeSelectionSource', 'isActive', 'themePreviewImage', 'themeConfig',
    'colors', 'tags', 'industry', 'category', 'price', 'rating', 'reviews',
    'image', 'tagline', 'layoutType', 'sectionDensity'
  ]);

  Object.entries(theme).forEach(([field, value]) => {
    if (value === undefined || value === null) return;

    const lowered = field.toLowerCase();
    if (skipKeys.has(field)) return;

    if (typeof value === 'string' || typeof value === 'number') {
      if (lowered.includes('color') || lowered.includes('font') || lowered.includes('style') || lowered.includes('variant')) {
        return;
      }
      result[field] = value;
    }
  });

  return result;
};

// Modern action button component
const ActionButton = ({ onClick, variant = "primary", icon, children, disabled, loading }) => {
  const baseStyles = "inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2";
  
  const variants = {
    primary: "bg-gradient-to-r from-indigo-600 to-indigo-500 text-white shadow-sm hover:shadow-md hover:from-indigo-500 hover:to-indigo-400 focus:ring-indigo-500",
    secondary: "bg-white text-gray-700 border border-gray-300 shadow-sm hover:bg-gray-50 hover:border-gray-400 focus:ring-gray-500",
    danger: "bg-white text-red-600 border border-red-200 shadow-sm hover:bg-red-50 hover:border-red-300 focus:ring-red-500",
    success: "bg-gradient-to-r from-emerald-600 to-emerald-500 text-white shadow-sm hover:shadow-md hover:from-emerald-500 hover:to-emerald-400 focus:ring-emerald-500",
    outline: "bg-transparent text-indigo-600 border border-indigo-200 hover:bg-indigo-50 focus:ring-indigo-500",
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseStyles} ${variants[variant]} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      {loading ? (
        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      ) : icon}
      {children}
    </button>
  );
};

// Modern badge component
const StatusBadge = ({ isActive }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
    isActive 
      ? "bg-emerald-50 text-emerald-700 border border-emerald-200" 
      : "bg-gray-100 text-gray-600 border border-gray-200"
  }`}>
    <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-500 animate-pulse" : "bg-gray-400"}`} />
    {isActive ? "Editing Active" : "View Only"}
  </span>
);

export default function ThemePreviewPage() {
  const { bussinessUuid, businessName, themeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [resolvedBusinessName, setResolvedBusinessName] = useState(businessName || "");
  const [storeInfo, setStoreInfo] = useState(null);
  const [storeTheme, setStoreTheme] = useState(null);
  const [storeBanner, setStoreBanner] = useState(null);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [themeText, setThemeText] = useState({});
  const [globalThemeText, setGlobalThemeText] = useState({});
  const [previewEditMode, setPreviewEditMode] = useState(false);
  const [activeEditField, setActiveEditField] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("products");
  const [searchQuery, setSearchQuery] = useState("");
  const [showOverview, setShowOverview] = useState(false);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [themesById, setThemesById] = useState({});

  // Get the correct image URL - EXACTLY like ThemeGallery's handleApply
  const getThemeImageUrl = useCallback((theme, savedTheme = null) => {
    // Priority order exactly matching ThemeGallery's handleApply:
    // 1. Saved theme's themePreviewImage
    // 2. Theme from gallery's image
    // 3. THEME_IMAGES map by theme ID
    // 4. Dawn fallback
    
    const savedImage = savedTheme?.themePreviewImage;
    if (savedImage && savedImage.startsWith('http')) {
      return savedImage;
    }
    
    const galleryImage = theme?.image;
    if (galleryImage && galleryImage.startsWith('http')) {
      return galleryImage;
    }
    
    const mappedImage = THEME_IMAGES[theme?.id];
    if (mappedImage) {
      return mappedImage;
    }
    
    return THEME_IMAGES.dawn;
  }, []);

  // Function to load saved theme data for a specific theme ID
  const loadSavedThemeData = useCallback(async (targetThemeId, businessUuidParam, businessData) => {
    try {
      const settingsResponse = await storeSettingsService.getStoreSettings(businessUuidParam);
      if (settingsResponse.success) {
        const settingsData = settingsResponse.data || {};
        
        let savedThemes = {};
        try {
          const themesByIdData = settingsData.themesById || {};
          if (typeof themesByIdData.get === 'function') {
            for (const [key, value] of themesByIdData.entries()) {
              savedThemes[key] = value;
            }
          } else {
            savedThemes = themesByIdData;
          }
        } catch (e) {
          console.warn('Failed to resolve themesById', e);
        }
        
        setThemesById(savedThemes);
        
        const activeThemeText = getThemeText(settingsData.theme || {});
        setGlobalThemeText(activeThemeText);
        
        let savedTheme = savedThemes[targetThemeId] || {};
        
        if (Object.keys(savedTheme).length === 0 && settingsData.theme && settingsData.theme.themeId === targetThemeId) {
          savedTheme = settingsData.theme || {};
        }
        
        setStoreTheme(savedTheme);
        setStoreBanner(settingsData.bannerImage || settingsData.banner || null);
        
        const savedTextValues = getThemeText(savedTheme);
        const initialTextValues = Object.keys(savedTheme).length === 0 ? activeThemeText : savedTextValues;
        setThemeText(prev => ({
          ...prev,
          ...initialTextValues,
          businessName: savedTextValues.businessName || businessData?.businessName || resolvedBusinessName,
        }));
        
        return savedTheme;
      }
    } catch (error) {
      console.warn('Failed to load saved theme data:', error);
    }
    return null;
  }, [resolvedBusinessName]);

  // Load preview data
  useEffect(() => {
    const loadPreviewData = async () => {
      setLoading(true);

      try {
        const businessResponse = await getBusiness(bussinessUuid);
        const fetchedBusinessName = businessResponse?.data?.businessName || "";
        if (fetchedBusinessName) {
          setResolvedBusinessName(fetchedBusinessName);
        }
        setStoreInfo(businessResponse?.data || null);

        await loadSavedThemeData(themeId, bussinessUuid, businessResponse?.data);

        try {
          const productsResponse = await getProducts(bussinessUuid);
          let productsData = [];
          if (productsResponse?.data?.data) {
            productsData = productsResponse.data.data;
          } else if (productsResponse?.data) {
            productsData = productsResponse.data;
          }
          setProducts(productsData || []);
        } catch (error) {
          console.warn("Failed to load preview products", error);
          setProducts([]);
        }

        try {
          const servicesResponse = await getServices(bussinessUuid);
          let servicesData = [];
          if (servicesResponse?.data?.data) {
            servicesData = servicesResponse.data.data;
          } else if (servicesResponse?.data) {
            servicesData = servicesResponse.data;
          }
          setServices(servicesData || []);
        } catch (error) {
          console.warn("Failed to load preview services", error);
          setServices([]);
        }
      } catch (error) {
        console.error("Failed to load preview data", error);
        toast.error(t("SETTINGS.THEME_STORE.FAILED_LOAD_PREVIEW") || "Failed to load preview data");
      } finally {
        setLoading(false);
      }
    };

    if (themeId && bussinessUuid) {
      loadPreviewData();
    }
  }, [bussinessUuid, themeId, t, loadSavedThemeData]);

  const selectedTheme = useMemo(
    () => location.state?.theme || SHOPIFY_THEMES.find((theme) => theme.id === themeId) || SHOPIFY_THEMES[0],
    [location.state, themeId],
  );

  // Preview theme with proper image handling - EXACTLY like ThemeGallery
  const previewTheme = useMemo(() => {
    if (!selectedTheme) return null;
    
    // Get the correct image URL using the same logic as ThemeGallery
    const savedThemeData = themesById[selectedTheme.id] || {};
    const imageUrl = getThemeImageUrl(selectedTheme, savedThemeData);
    
    const layoutPreset = THEME_LAYOUT_PRESETS[selectedTheme.id] || {};
    
    const mergedTheme = {
      ...selectedTheme.themeConfig,
      ...savedThemeData,
      ...storeTheme,
      ...globalThemeText,
      ...themeText,
      themeName: selectedTheme.name,
      themeId: selectedTheme.id,
      themePreviewImage: imageUrl,
      image: imageUrl,
      layoutPreset,
      heroVariant: layoutPreset.heroVariant || "classic",
      productVariant: layoutPreset.productVariant || "grid",
      hideBanner: Boolean(storeBanner) ? false : true,
    };
    
    return mergedTheme;
  }, [selectedTheme, themesById, storeTheme, themeText, storeBanner, globalThemeText, getThemeImageUrl]);

  const handleTextChange = useCallback((key, value) => {
    if (!previewEditMode) return;
    
    setThemeText((prev) => ({
      ...prev,
      [key]: value,
    }));
    
    setStoreTheme((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, [previewEditMode]);

  const handleActivateField = useCallback((field) => {
    if (!previewEditMode) return;
    setActiveEditField(field);
  }, [previewEditMode]);

  const handleDeactivateField = useCallback(() => {
    if (!previewEditMode) return;
    setActiveEditField(null);
  }, [previewEditMode]);

  const togglePreviewEditMode = useCallback(() => {
    const nextState = !previewEditMode;
    setPreviewEditMode(nextState);
    if (!nextState) {
      setActiveEditField(null);
    }
  }, [previewEditMode]);

  const handleSavePreviewText = useCallback(async () => {
    if (!previewTheme) return;

    setSaving(true);
    try {
      const themeToSave = {
        ...previewTheme,
        ...themeText,
        themeSelectionSource: "preset",
      };
      
      delete themeToSave.previewEditMode;
      delete themeToSave.activeEditField;
      delete themeToSave.enablePreviewEditMode;
      delete themeToSave.onPreviewTextChange;
      delete themeToSave.onActivateField;
      delete themeToSave.onDeactivateField;
      
      const response = await storeSettingsService.updateTheme(bussinessUuid, themeToSave);

      if (response.success) {
        setStoreTheme(themeToSave);
        toast.success(response.message || t("SETTINGS.THEME_STORE.PREVIEW_TEXT_SAVED", "Preview text saved successfully."));
        
        const verifyResponse = await storeSettingsService.getStoreSettings(bussinessUuid);
        if (verifyResponse.success) {
          let updatedThemes = {};
          try {
            const themesByIdData = verifyResponse.data.themesById || {};
            if (typeof themesByIdData.get === 'function') {
              for (const [key, value] of themesByIdData.entries()) {
                updatedThemes[key] = value;
              }
            } else {
              updatedThemes = themesByIdData;
            }
          } catch (e) {
            console.warn('Failed to parse themesById', e);
          }
          
          setThemesById(updatedThemes);
          
          const savedVersion = updatedThemes[selectedTheme?.id] || verifyResponse.data.theme;
          if (savedVersion) {
            setStoreTheme(savedVersion);
            const savedTextValues = getThemeText(savedVersion);
            setThemeText(prev => ({
              ...prev,
              ...savedTextValues,
            }));
          }
        }
      } else {
        toast.error(response.error || t("SETTINGS.THEME_STORE.FAILED_SAVE_PREVIEW_TEXT", "Failed to save preview text."));
      }
    } catch (error) {
      console.error("Save preview text failed", error);
      toast.error(t("SETTINGS.THEME_STORE.FAILED_SAVE_PREVIEW_TEXT", "Failed to save preview text."));
    } finally {
      setSaving(false);
    }
  }, [previewTheme, themeText, bussinessUuid, t, selectedTheme]);

  const openStorefrontPreview = useCallback(() => {
    const url = `${getStorefrontPath({ businessName: resolvedBusinessName, bussinessUuid })}?previewTheme=${selectedTheme?.id}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }, [resolvedBusinessName, bussinessUuid, selectedTheme?.id]);

  // CRITICAL FIX: handleLaunchTheme - EXACTLY like ThemeGallery's handleApply
  const handleLaunchTheme = useCallback(async () => {
    if (!selectedTheme) return;

    setSaving(true);
    try {
      const layoutPreset = THEME_LAYOUT_PRESETS[selectedTheme.id] || THEME_LAYOUT_PRESETS.dawn;
      
      // Get saved theme data and image URL exactly like ThemeGallery
      const savedThemeData = themesById[selectedTheme.id] || {};
      const imageUrl = getThemeImageUrl(selectedTheme, savedThemeData);
      
      let themeToApply;
      
      // Check if we have saved theme data (exactly like ThemeGallery)
      if (Object.keys(savedThemeData).length > 0) {
        // Use saved version with all text edits preserved
        themeToApply = {
          ...savedThemeData,
          themePreviewImage: imageUrl,
          layoutPreset,
          heroVariant: savedThemeData.heroVariant || layoutPreset.heroVariant,
          productVariant: savedThemeData.productVariant || layoutPreset.productVariant,
          themeSelectionSource: "preset",
          isActive: true,
        };
        console.log("Launch: Using saved theme with edits:", savedThemeData);
      } else {
        // Use fresh theme with image (exactly like ThemeGallery)
        themeToApply = {
          ...selectedTheme.themeConfig,
          themeName: selectedTheme.name,
          themeId: selectedTheme.id,
          themePreviewImage: imageUrl,
          layoutPreset,
          heroVariant: layoutPreset.heroVariant,
          productVariant: layoutPreset.productVariant,
          hideBanner: true,
          themeSelectionSource: "preset",
          isActive: true,
        };
        console.log("Launch: Using fresh theme:", selectedTheme.name, "with image:", imageUrl);
      }
      
      // Merge any edited text from preview
      if (themeText && Object.keys(themeText).length > 0) {
        themeToApply = {
          ...themeToApply,
          ...themeText,
        };
      }
      
      // Remove any preview-specific properties
      delete themeToApply.previewEditMode;
      delete themeToApply.activeEditField;
      delete themeToApply.enablePreviewEditMode;
      delete themeToApply.onPreviewTextChange;
      delete themeToApply.onActivateField;
      delete themeToApply.onDeactivateField;
      
      console.log("Launch: Saving theme to backend with image:", themeToApply.themePreviewImage);
      
      const response = await storeSettingsService.updateTheme(bussinessUuid, themeToApply);

      if (response.success) {
        setGlobalThemeText(getThemeText(themeToApply));
        toast.success(response.message || t("SETTINGS.THEME_STORE.THEME_LAUNCHED_SUCCESS"));
        
        // Navigate back to themes page after a short delay
        setTimeout(() => {
          navigate(`/${resolvedBusinessName}/${bussinessUuid}/online-store/themes`);
        }, 500);
      } else {
        toast.error(response.error || t("SETTINGS.THEME_STORE.FAILED_APPLY_THEME"));
      }
    } catch (error) {
      console.error("Launch theme failed", error);
      toast.error(t("SETTINGS.THEME_STORE.FAILED_APPLY_THEME") || "Could not launch theme");
    } finally {
      setSaving(false);
    }
  }, [selectedTheme, themesById, themeText, bussinessUuid, resolvedBusinessName, navigate, t, getThemeImageUrl]);

  const handleContainerClick = useCallback((e) => {
    if (previewEditMode) {
      handleDeactivateField();
    }
  }, [previewEditMode, handleDeactivateField]);

  // Get theme preview image function - EXACTLY like ThemeGallery
  const getThemePreviewImage = useCallback(() => {
    const savedThemeData = themesById[selectedTheme?.id] || {};
    const imageUrl = getThemeImageUrl(selectedTheme, savedThemeData);
    return imageUrl;
  }, [selectedTheme, themesById, getThemeImageUrl]);

  useEffect(() => {
    if (loading) {
      document.body.style.overflow = "hidden";
      document.body.style.overflowX = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.body.style.overflowX = "";
      document.documentElement.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.overflowX = "";
      document.documentElement.style.overflow = "";
    };
  }, [loading]);

  if (loading) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-6 h-6 bg-indigo-100 rounded-full animate-pulse" />
            </div>
          </div>
          <p className="text-gray-500 font-medium">Loading your theme preview...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Modern Glassmorphism Top Bar */}
      <div className="sticky top-0 z-50 w-full backdrop-blur-xl bg-white/80 dark:bg-gray-900/80 border-b border-gray-200/50 shadow-sm">
        <div className="w-full px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Left Section - Theme Info */}
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-lg">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  {selectedTheme?.name}
                </h1>
                <p className="text-xs text-gray-500">Theme Preview • Customize before publishing</p>
              </div>
            </div>

            {/* Right Section - Actions */}
            <div className="flex items-center gap-3">
              <StatusBadge isActive={previewEditMode} />
              
              <div className="h-6 w-px bg-gray-200" />
              
              <ActionButton
                onClick={() => navigate(`/${resolvedBusinessName}/${bussinessUuid}/online-store/themes`)}
                variant="secondary"
                icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>}
              >
                Back
              </ActionButton>
              
              <ActionButton
                onClick={togglePreviewEditMode}
                variant={previewEditMode ? "danger" : "outline"}
                icon={previewEditMode ? 
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg> :
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                }
              >
                {previewEditMode ? "Exit Edit" : "Edit Text"}
              </ActionButton>
              
              {previewEditMode && (
                <ActionButton
                  onClick={handleSavePreviewText}
                  variant="success"
                  loading={saving}
                  icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}
                  disabled={saving}
                >
                  Save
                </ActionButton>
              )}
              
              <ActionButton
                onClick={handleLaunchTheme}
                variant="primary"
                loading={saving}
                icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>}
                disabled={saving}
              >
                Launch
              </ActionButton>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Status Indicator */}
      {previewEditMode && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-gray-900/95 backdrop-blur-md text-white px-5 py-2.5 rounded-full text-sm shadow-xl flex items-center gap-3 border border-white/10">
            <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse shadow-glow" />
            <span className="font-medium">Edit Mode Active</span>
            <span className="text-gray-400 text-xs">Click any text to edit</span>
          </div>
        </div>
      )}
      
      {!previewEditMode && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-gray-800/80 backdrop-blur-md text-gray-200 px-5 py-2.5 rounded-full text-sm shadow-xl flex items-center gap-3 border border-white/5">
            <span className="w-2 h-2 bg-gray-400 rounded-full" />
            <span className="font-medium">View Mode</span>
            <span className="text-gray-400 text-xs">Click "Edit Text" to make changes</span>
          </div>
        </div>
      )}

      {/* Theme Preview Container */}
      <div className="relative" onClick={handleContainerClick}>
        {/* Subtle gradient overlay for depth */}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-transparent to-gray-100/50" />
          <ThemeLayoutContent
            layoutType={getLayoutType(selectedTheme?.id || "velora")}
            layoutProps={{
            t,
            selectedTheme: selectedTheme,
            storeInfo: {
              ...storeInfo,
              businessName: themeText.businessName || storeInfo?.businessName,
            },
            storeTheme: previewTheme,
            storeBanner,
            previewEditMode,
            onPreviewTextChange: handleTextChange,
            activeEditField,
            onActivateField: handleActivateField,
            onDeactivateField: handleDeactivateField,
            enablePreviewEditMode: () => setPreviewEditMode(true),
            allProducts: products,
            filteredProducts: products,
            allServices: services,
            filteredServices: services,
            storeDescription: storeInfo?.businessDescription || "",
            cart: [],
            userProfile: null,
            activeTab,
            setActiveTab,
            searchQuery,
            setSearchQuery,
            showOverview,
            setShowOverview,
            filterPanelOpen,
            setFilterPanelOpen,
            mobileMenuOpen,
            setMobileMenuOpen,
            bussinessUuid,
            displayBusinessName: resolvedBusinessName,
            resolvedThemeId: resolveThemeId(previewTheme || {}),
            storefrontPreset: getStorefrontThemePreset(previewTheme || {}),
            shouldUseStoreBanner: Boolean(storeBanner),
            getBannerUrl: () => {
              if (!storeBanner) return null;
              if (storeBanner.type === "color") return null;
              if (storeBanner.type === "image" && storeBanner.imageUrl) {
                let url = storeBanner.imageUrl;
                if (url && !url.startsWith("http") && !url.startsWith("https")) {
                  if (url.startsWith("/")) {
                    url = `${window.location.origin}${url}`;
                  } else if (url.includes("/")) {
                    url = `https://dukanbuddy-normal.s3.us-east-1.amazonaws.com/${url}`;
                  }
                }
                return url;
              }
              return null;
            },
            getThemePreviewImage: getThemePreviewImage,
            heroVariant: previewTheme?.heroVariant || "classic",
            resolvedProductVariant: previewTheme?.productVariant || "grid",
            themeStyles: {
              fontFamily: previewTheme?.fontFamily || "Inter, system-ui, -apple-system, sans-serif",
              backgroundColor: previewTheme?.backgroundColor || "#f9fafb",
              color: previewTheme?.textColor || "#1f2937",
            },
            headingStyle: {
              color: previewTheme?.headingColor || "#111827",
              fontFamily: previewTheme?.fontFamily || "Inter, system-ui, -apple-system, sans-serif",
            },
            buttonStyle: {
              backgroundColor: previewTheme?.buttonColor || "#4f46e5",
              color: "#ffffff",
              borderRadius: previewTheme?.buttonStyle === "pill"
                ? "9999px"
                : previewTheme?.buttonStyle === "rounded"
                  ? "8px"
                  : "4px",
              fontFamily: previewTheme?.fontFamily || "Inter, system-ui, -apple-system, sans-serif",
            },
            handleAddToCart: () => {},
            handleOpenCart: () => {},
            handleViewOrders: () => {},
            handleViewProfile: () => {},
            handleOpenLogin: () => {},
            handleLogout: () => {},
            handleBackToLanding: () => {},
            handleGoToWishlist: () => {},
            handleWishlist: () => {},
            setFilteredProducts: setProducts,
            wishlistCount: 0,
            wishlistLoading: false,
            overviewContent: storeInfo?.businessDescription || "",
            storefrontText: {
              back: t("BACK", "Back"),
              products: t("ONLINE_STORE.PRODUCTS", "Products"),
              services: t("ONLINE_STORE.SERVICES", "Services"),
              about: t("ONLINE_STORE.ABOUT_US", "About Us"),
              storeInfo: t("STORE.STORE_INFO", "Store Info"),
              searchProducts: t("STORE.SEARCH_PRODUCTS", "Search products"),
              cart: t("STORE.CART", "Cart"),
              filter: t("STORE.FILTERS", "Filters"),
              wishlist: t("WISHLIST.TITLE", "Wishlist"),
              showMore: t("ONLINE_STORE.SHOW_MORE", "Show More"),
              showLess: t("ONLINE_STORE.SHOW_LESS", "Show Less"),
              contact: t("ONLINE_STORE.CONTACT_INFORMATION", "Contact"),
              business: t("ONLINE_STORE.BUSINESS_DETAILS", "Business"),
              businessType: t("ONLINE_STORE.BUSINESS_TYPE", "Business Type"),
              notProvided: t("ONLINE_STORE.NOT_PROVIDED", "Not provided"),
              callNow: t("ONLINE_STORE.CALL_NOW", "Call Now"),
              whatsapp: t("ONLINE_STORE.WATSAPP", "WhatsApp"),
              clearSearch: t("STORE.CLEAR_SEARCH", "Clear Search"),
              noProductsMatch: t("STORE.NO_PRODUCTS_MATCH_SEARCH", "No products match your search"),
              noProductsAvailable: t("ONLINE_STORE.NO_PRODUCT_AVAILABLE", "No products available yet"),
              noServicesMatch: t("STORE.NO_SERVICES_MATCH_SEARCH", "No services match your search"),
              noServicesAvailable: t("ONLINE_STORE.NO_SERVICES_AVAILABLE", "No services available yet"),
              shop: t("ONLINE_STORE.PRODUCTS", "Shop"),
              menu: t("ONLINE_STORE.PRODUCTS", "Menu"),
              shopNow: t("SETTINGS.THEME_STORE.SHOP_NOW", "Shop Now"),
              learnMore: t("SETTINGS.THEME_STORE.ABOUT", "Learn More"),
              viewAll: t("STORE.VIEW_ALL", "View all"),
              featuredCollection: t("STORE.FEATURED_COLLECTION", "Featured Collection"),
              exploreAllProducts: t("SETTINGS.THEME_STORE.EXPLORE_ALL_PRODUCTS", "Explore All Products"),
            },
            userMenuElement: null,
            removeToast: () => {},
            toasts: [],
            previewMode: true,
          }}
        />
      </div>
    </div>
  );
}